import React, { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { io } from "socket.io-client";
import PdfDownloadPage from "./PdfPreviewPage";

const ROOM_URL = "https://ini.daily.co/test";
const userId = "user-12345"; // Set dynamically in your app
const userDisplayName = "driver"; // Set dynamically in your app/context

export default function UserJoin() {
  const frameRef = useRef(null);
  const frameElem = useRef(null);
  const socketRef = useRef(null);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const theme = {
    colors: {
      accent: "#1F74EC",
      accentText: "#172447",
      background: "#111725",
      backgroundAccent: "#F692AC",
      baseText: "#F7F7F7",
      border: "#728ED8",
      mainAreaBg: "#172447",
      mainAreaBgAccent: "#334985",
      mainAreaText: "#EEEEEE",
      supportiveText: "#ADCFFF",
    },
  };

  // This check is robust against race conditions and only leaves the local user if they're a second driver
  const enforceOneDriver = () => {
    if (!frameRef.current) return;
    const participants = frameRef.current.participants();
    const participantArray = Object.values(participants);
    // Find myself (local participant)
    const local = participantArray.find((p) => p.local);

    // Only enforce if the local participant's name is 'driver'
    if (
      !local ||
      !local.user_name ||
      local.user_name.toLowerCase() !== "driver"
    )
      return;

    // Count the number of drivers (including myself)
    const drivers = participantArray.filter(
      (p) =>
        p.user_name &&
        typeof p.user_name === "string" &&
        p.user_name.toLowerCase() === "driver"
    );

    // If there are more than one drivers and I am local, and I'm not the longest-standing driver, I kick myself
    // The solution: only allow the 'oldest' driver, others (i.e., last join) must leave
    if (drivers.length > 1) {
      // In Daily, session_id or id can be used for identity; lowest value is typically the oldest
      // We'll use session_id, fallback to id.
      const sorted = drivers
        .map((p) => p.session_id || p.id)
        .filter(Boolean)
        .sort(); // ascending order: oldest first
      const myId = local.session_id || local.id;
      if (myId && sorted.length > 1 && sorted[0] !== myId) {
        // I am NOT the oldest/first driver, so I must leave
        frameRef.current.leave();
        setJoined(false);
        setError(
          "Room already has a driver. Only one driver allowed at a time."
        );
      }
    }
  };

  useEffect(() => {
    socketRef.current = io("https://iniserve.com", {
      transports: ["websocket"],
    });

    socketRef.current.on("connect", () => {
      socketRef.current.emit("register", userId);
    });

    socketRef.current.on("open-url", (url) => {
      if (typeof url === "string" && url.toLowerCase().endsWith(".pdf")) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        setPdfUrl(url);
      } else {
        window.location.href = url;
      }
    });

    socketRef.current.on("kick-participant", (data) => {
      if (data && data.userId === userId) {
        alert("You have been removed from the room by admin.");
        if (frameRef.current) frameRef.current.leave();
        setJoined(false);
      }
    });

    fetch("https://iniserve.com/api/room/getmytoken?role=user")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
        const data = await res.json();
        if (!data.token) throw new Error("No token in response");

        if (frameRef.current) {
          frameRef.current.leave();
          frameRef.current.destroy();
        }

        frameRef.current = DailyIframe.createFrame(frameElem.current, {
          theme,
          iframeStyle: {
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            border: "0",
          },
          showLeaveButton: true,
          showFullscreenButton: true,
          videoSource: false,
          audioSource: true,
          startVideoOff: true,
          showLocalVideo: false,
        });

        try {
          await frameRef.current.join({ url: ROOM_URL, token: data.token });
          // Delay: let the participant list settle for worst-case race
          setTimeout(() => {
            enforceOneDriver();
            setJoined(true);
            setError(null);
            socketRef.current.emit("user-joined-room", { userId });
          }, 500);

          // Also trigger on any participant joins
          frameRef.current.on("participant-joined", enforceOneDriver);
        } catch (err) {
          const isFull =
            err.message?.toLowerCase().includes("room is full") ||
            err.message?.toLowerCase().includes("max participants");
          setError(
            isFull
              ? "Room is full, please wait."
              : "Failed to join the room. Please try again later."
          );
          setJoined(false);
        }
      })
      .catch(() => {
        setError("Failed to get room token.");
        setJoined(false);
      });

    return () => {
      if (frameRef.current) {
        frameRef.current.leave();
        frameRef.current.destroy();
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
    // eslint-disable-next-line
  }, []);

  if (pdfUrl) {
    return <PdfDownloadPage url={pdfUrl} />;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "#000",
        margin: 0,
        padding: 0,
        zIndex: 9999,
        overflow: "hidden",
        touchAction: "none",
      }}
    >
      {!joined && (
        <div
          style={{
            color: "#fff",
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2,
            fontSize: 20,
            textAlign: "center",
            padding: "0 20px",
          }}
        >
          {error ? <>Error: {error}</> : <>Joining room...</>}
        </div>
      )}
      <div
        ref={frameElem}
        style={{
          position: "absolute",
          inset: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 1,
          background: "#000",
        }}
      />
    </div>
  );
}

// import React, { useEffect, useRef, useState } from "react";
// import DailyIframe from "@daily-co/daily-js";
// import { io } from "socket.io-client";
// import PdfDownloadPage from "./PdfPreviewPage";

// const ROOM_URL = "https://ini.daily.co/test";
// const userId = "user-12345"; // Set dynamically in your app

// export default function UserJoin() {
//   const frameRef = useRef(null);
//   const frameElem = useRef(null);
//   const socketRef = useRef(null);
//   const [joined, setJoined] = useState(false);
//   const [error, setError] = useState(null);
//   const [pdfUrl, setPdfUrl] = useState(null);

//   const theme = {
//     colors: {
//       accent: "#1F74EC",
//       accentText: "#172447",
//       background: "#111725",
//       backgroundAccent: "#F692AC",
//       baseText: "#F7F7F7",
//       border: "#728ED8",
//       mainAreaBg: "#172447",
//       mainAreaBgAccent: "#334985",
//       mainAreaText: "#EEEEEE",
//       supportiveText: "#ADCFFF",
//     },
//   };

//   const enforceSingleAdminAndDriver = () => {
//     if (!frameRef.current) return;
//     const participants = frameRef.current.participants();
//     const participantArray = Object.values(participants);

//     const local = participantArray.find((p) => p.local);
//     if (!local || !local.user_name) return;
//     const localUserName = local.user_name.toLowerCase();

//     // Participants by role
//     const drivers = participantArray.filter(
//       (p) => p.user_name && p.user_name.toLowerCase() === "driver"
//     );
//     const admins = participantArray.filter(
//       (p) => p.user_name && p.user_name.toLowerCase() === "cbm"
//     );

//     // If local is driver and there are multiple drivers, only oldest driver stays, newer leave
//     if (localUserName === "driver" && drivers.length > 1) {
//       const sortedDrivers = drivers
//         .map((p) => p.session_id || p.id)
//         .filter(Boolean)
//         .sort();
//       const myId = local.session_id || local.id;
//       if (myId && sortedDrivers.length && sortedDrivers[0] !== myId) {
//         // Local driver is newer, leave
//         frameRef.current.leave();
//         setJoined(false);
//         setError("Room already has a driver. Only one driver allowed at a time.");
//         return;
//       }
//     }

//     // If local is driver and there are multiple admins (shouldn't happen), leave
//     if (localUserName === "driver" && admins.length > 1) {
//       frameRef.current.leave();
//       setJoined(false);
//       setError("Room already has an admin. Only one admin allowed at a time.");
//       return;
//     }
//   };

//   useEffect(() => {
//     socketRef.current = io("https://iniserve.com", {
//       transports: ["websocket"],
//     });

//     socketRef.current.on("connect", () => {
//       socketRef.current.emit("register", userId);
//     });

//     socketRef.current.on("open-url", (url) => {
//       if (typeof url === "string" && url.toLowerCase().endsWith(".pdf")) {
//         window.history.replaceState({}, document.title, window.location.pathname);
//         setPdfUrl(url);
//       } else {
//         window.location.href = url;
//       }
//     });

//     socketRef.current.on("kick-participant", (data) => {
//       if (data && data.userId === userId) {
//         alert("You have been removed from the room by admin.");
//         if (frameRef.current) frameRef.current.leave();
//         setJoined(false);
//       }
//     });

//     fetch("https://iniserve.com/api/room/getmytoken?role=user")
//       .then(async (res) => {
//         if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
//         const data = await res.json();
//         if (!data.token) throw new Error("No token in response");

//         if (frameRef.current) {
//           frameRef.current.leave();
//           frameRef.current.destroy();
//         }

//         frameRef.current = DailyIframe.createFrame(frameElem.current, {
//           theme,
//           iframeStyle: {
//             position: "absolute",
//             width: "100%",
//             height: "100%",
//             top: 0,
//             left: 0,
//             border: "0",
//           },
//           showLeaveButton: true,
//           showFullscreenButton: true,
//           videoSource: false,
//           audioSource: true,
//           startVideoOff: true,
//           showLocalVideo: false,
//         });

//         try {
//           await frameRef.current.join({ url: ROOM_URL, token: data.token });

//           setTimeout(() => {
//             enforceSingleAdminAndDriver();
//             setJoined(true);
//             setError(null);
//             socketRef.current.emit("user-joined-room", { userId });
//           }, 500);

//           frameRef.current.on("participant-joined", enforceSingleAdminAndDriver);
//           frameRef.current.on("participant-left", enforceSingleAdminAndDriver);
//           frameRef.current.on("participants-changed", enforceSingleAdminAndDriver);
//           frameRef.current.on("joined-meeting", enforceSingleAdminAndDriver);
//         } catch (err) {
//           const isFull =
//             err.message?.toLowerCase().includes("room is full") ||
//             err.message?.toLowerCase().includes("max participants");
//           setError(isFull ? "Room is full, please wait." : "Failed to join the room. Please try again later.");
//           setJoined(false);
//         }
//       })
//       .catch(() => {
//         setError("Failed to get room token.");
//         setJoined(false);
//       });

//     return () => {
//       if (frameRef.current) {
//         frameRef.current.leave();
//         frameRef.current.destroy();
//       }
//       if (socketRef.current) {
//         socketRef.current.disconnect();
//       }
//     };
//   }, []);

//   if (pdfUrl) {
//     return <PdfDownloadPage url={pdfUrl} />;
//   }

//   return (
//     <div style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", background: "#000", margin: 0, padding: 0, zIndex: 9999, overflow: "hidden", touchAction: "none" }}>
//       {!joined && (
//         <div style={{ color: "#fff", position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", zIndex: 2, fontSize: 20, textAlign: "center", padding: "0 20px" }}>
//           {error ? <>Error: {error}</> : <>Joining room...</>}
//         </div>
//       )}
//       <div ref={frameElem} style={{ position: "absolute", inset: 0, width: "100vw", height: "100vh", zIndex: 1, background: "#000" }} />
//     </div>
//   );
// }

// import React, { useEffect, useRef, useState } from "react";
// import DailyIframe from "@daily-co/daily-js";
// import { io } from "socket.io-client";
// import PdfDownloadPage from "./PdfPreviewPage";

// const ROOM_URL = "https://ini.daily.co/test";
// const userId = "user-12345"; // Set dynamically in your app
// // const userDisplayName = "driver"; // Set dynamically in your app/context

// export default function UserJoin() {
//   const frameRef = useRef(null);
//   const frameElem = useRef(null);
//   const socketRef = useRef(null);
//   const [joined, setJoined] = useState(false);
//   const [error, setError] = useState(null);
//   const [pdfUrl, setPdfUrl] = useState(null);

//   const theme = {
//     colors: {
//       accent: "#1F74EC",
//       accentText: "#172447",
//       background: "#111725",
//       backgroundAccent: "#F692AC",
//       baseText: "#F7F7F7",
//       border: "#728ED8",
//       mainAreaBg: "#172447",
//       mainAreaBgAccent: "#334985",
//       mainAreaText: "#EEEEEE",
//       supportiveText: "#ADCFFF",
//     },
//   };

//   // Enforce only one driver and one admin in the room at a time
//   const enforceSingleAdminAndDriver = () => {
//     if (!frameRef.current) return;
//     const participants = frameRef.current.participants();
//     const participantArray = Object.values(participants);

//     const local = participantArray.find((p) => p.local);
//     if (!local || !local.user_name) return;
//     const localUserName = local.user_name.toLowerCase();

//     // Get all drivers and admins in room
//     const drivers = participantArray.filter(
//       (p) => p.user_name && p.user_name.toLowerCase() === "driver"
//     );
//     const admins = participantArray.filter(
//       (p) => p.user_name && p.user_name.toLowerCase() === "cbm"
//     );

//     // Debug logs (can be removed after testing)
//     // console.log("UserJoin enforce: drivers", drivers.length, "admins", admins.length);

//     // If local participant is driver and there are multiple drivers,
//     // only the oldest driver stays, others leave
//     if (localUserName === "driver" && drivers.length > 1) {
//       const sortedDrivers = drivers
//         .map((p) => p.session_id || p.id)
//         .filter(Boolean)
//         .sort();
//       const myId = local.session_id || local.id;
//       if (myId && sortedDrivers.length && sortedDrivers[0] !== myId) {
//         frameRef.current.leave();
//         setJoined(false);
//         setError(
//           "Room already has a driver. Only one driver allowed at a time."
//         );
//         return;
//       }
//     }

//     // If local participant is driver and admins > 1 (shouldn't happen), leave
//     if (localUserName === "driver" && admins.length > 1) {
//       frameRef.current.leave();
//       setJoined(false);
//       setError("Room already has an admin. Only one admin allowed at a time.");
//       return;
//     }

//     // If local participant is driver and admins > 0, this is valid (one admin allowed)

//     // Extra: If this is local driver but no admins yet, allow join (no action)
//   };

//   useEffect(() => {
//     socketRef.current = io("https://iniserve.com", {
//       transports: ["websocket"],
//     });

//     socketRef.current.on("connect", () => {
//       socketRef.current.emit("register", userId);
//     });

//     socketRef.current.on("open-url", (url) => {
//       if (typeof url === "string" && url.toLowerCase().endsWith(".pdf")) {
//         window.history.replaceState(
//           {},
//           document.title,
//           window.location.pathname
//         );
//         setPdfUrl(url);
//       } else {
//         window.location.href = url;
//       }
//     });

//     socketRef.current.on("kick-participant", (data) => {
//       if (data && data.userId === userId) {
//         alert("You have been removed from the room by admin.");
//         if (frameRef.current) frameRef.current.leave();
//         setJoined(false);
//       }
//     });

//     fetch("https://iniserve.com/api/room/getmytoken?role=user")
//       .then(async (res) => {
//         if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
//         const data = await res.json();
//         if (!data.token) throw new Error("No token in response");

//         if (frameRef.current) {
//           frameRef.current.leave();
//           frameRef.current.destroy();
//         }

//         frameRef.current = DailyIframe.createFrame(frameElem.current, {
//           theme,
//           iframeStyle: {
//             position: "absolute",
//             width: "100%",
//             height: "100%",
//             top: 0,
//             left: 0,
//             border: "0",
//           },
//           showLeaveButton: true,
//           showFullscreenButton: true,
//           videoSource: false,
//           audioSource: true,
//           startVideoOff: true,
//           showLocalVideo: false,
//         });

//         try {
//           await frameRef.current.join({ url: ROOM_URL, token: data.token });

//           // Run enforcement shortly after join to handle race conditions
//           setTimeout(() => {
//             enforceSingleAdminAndDriver();
//             setJoined(true);
//             setError(null);
//             socketRef.current.emit("user-joined-room", { userId });
//           }, 500);

//           // Listen on participant events to re-enforce constraints
//           frameRef.current.on(
//             "participant-joined",
//             enforceSingleAdminAndDriver
//           );
//           frameRef.current.on("participant-left", enforceSingleAdminAndDriver);
//           frameRef.current.on(
//             "participants-changed",
//             enforceSingleAdminAndDriver
//           );
//           frameRef.current.on("joined-meeting", enforceSingleAdminAndDriver);
//         } catch (err) {
//           const isFull =
//             err.message?.toLowerCase().includes("room is full") ||
//             err.message?.toLowerCase().includes("max participants");
//           setError(
//             isFull
//               ? "Room is full, please wait."
//               : "Failed to join the room. Please try again later."
//           );
//           setJoined(false);
//         }
//       })
//       .catch(() => {
//         setError("Failed to get room token.");
//         setJoined(false);
//       });

//     return () => {
//       if (frameRef.current) {
//         frameRef.current.leave();
//         frameRef.current.destroy();
//       }
//       if (socketRef.current) {
//         socketRef.current.disconnect();
//       }
//     };
//     // eslint-disable-next-line
//   }, []);

//   if (pdfUrl) {
//     return <PdfDownloadPage url={pdfUrl} />;
//   }

//   return (
//     <div
//       style={{
//         position: "fixed",
//         inset: 0,
//         width: "100vw",
//         height: "100vh",
//         background: "#000",
//         margin: 0,
//         padding: 0,
//         zIndex: 9999,
//         overflow: "hidden",
//         touchAction: "none",
//       }}
//     >
//       {!joined && (
//         <div
//           style={{
//             color: "#fff",
//             position: "absolute",
//             left: "50%",
//             top: "50%",
//             transform: "translate(-50%, -50%)",
//             zIndex: 2,
//             fontSize: 20,
//             textAlign: "center",
//             padding: "0 20px",
//           }}
//         >
//           {error ? <>Error: {error}</> : <>Joining room...</>}
//         </div>
//       )}
//       <div
//         ref={frameElem}
//         style={{
//           position: "absolute",
//           inset: 0,
//           width: "100vw",
//           height: "100vh",
//           zIndex: 1,
//           background: "#000",
//         }}
//       />
//     </div>
//   );
// }
